import { useEffect, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/fp/isEmpty'

import cn from 'utils/classnames'
import { CardComparisonTableHorizontalSkeleton } from './loading-skeleton-card-comparison-table-horizontal'
import { HorizontelTable } from './card-table'
import { getSortedAndFilteredBySortPosition } from 'utils/sort/sortPosition'
import { useIsPinningActive } from 'utils/useIsPinningActive'
import { useIsUserOnBrave } from 'utils/useIsUserOnBrave'
import { pinEntities } from 'utils/pin-entities'
import { formatPinnedProducts } from 'ssg/utils/format-pinned-products'
import mapEntityPositions from 'utils/mapEntityPositions'

import { CompareCredit } from '../../../../types/compare-credit'

export const CardComparisonTableHorizontal = (data: {
  cardOrderIds: Record<string, string>
}) => {
  const CardComparisonTableHorizontalRender: React.FC<{
    value: {
      referencedCards: null | Record<string, CompareCredit.Entity>
      cards: CompareCredit.Entity[]
      tag: CompareCredit.CategoryTag
      categoryTag: CompareCredit.CategoryTag
      title: string
      pinnedCardTips?: CompareCredit.PinnedProduct[]
      cardOrderIds: Record<string, string>
      sort: string
    }
  }> = ({
    value: {
      categoryTag,
      tag,
      title,
      cards,
      sort,
      pinnedCardTips,
      referencedCards,
    },
  }) => {
    // 1. STICKY TABLE HEADER
    const [navHeight, setNavHeight] = useState(0)
    const [innerTableHeight, setInnerTableHeight] = useState(0)
    const [tableHeaderIsSticky, setTableHeaderIsSticky] = useState(false)

    const tableStickyHeaderRef = useRef<HTMLDivElement>(null)
    const tableHoriInnerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const nav = document.getElementById('navbar-container')
      if (nav) setNavHeight(nav.scrollHeight)

      const innerTable = document.getElementById('table-hori-inner')
      if (innerTable) {
        setInnerTableHeight(innerTable.scrollHeight)
      }
    }, [])

    // 1.1 = Sticky table header
    useEffect(() => {
      const tableHeaderTopPos =
        tableStickyHeaderRef.current === null
          ? 0
          : window.scrollY +
            tableStickyHeaderRef.current.getBoundingClientRect().top

      const handleStick = () => {
        setTableHeaderIsSticky(
          window.pageYOffset > tableHeaderTopPos - navHeight &&
            window.pageYOffset <
              tableHeaderTopPos + innerTableHeight - navHeight,
        )
      }

      window.addEventListener('scroll', handleStick)
      return () => {
        window.removeEventListener('scroll', handleStick)
      }
    }, [innerTableHeight, navHeight])

    // 1.1 = Move table header horizontally
    useEffect(() => {
      const handleScroll = (e: Event) => {
        const target = e.target as HTMLElement
        if (tableStickyHeaderRef.current) {
          tableStickyHeaderRef.current.scrollLeft = target.scrollLeft
        }
      }

      tableHoriInnerRef.current?.addEventListener('scroll', handleScroll)

      return () => {
        tableHoriInnerRef.current?.removeEventListener('scroll', handleScroll)
      }
    }, [tableStickyHeaderRef.current, tableHoriInnerRef.current])

    const { cardOrderIds } = data

    const isPinningActive = useIsPinningActive(sort)

    const userIsOnBrave = useIsUserOnBrave()

    const categoryId = categoryTag ? categoryTag.id : '00'
    const externalId = tag ? tag.id : '00'

    // 2. CARD DATA
    const cardSlugs = cards.map((card) => card.slug)

    const cardsData = useMemo(() => {
      const formattedCard = referencedCards
        ? cardSlugs.map((slug) => referencedCards[slug])
        : []

      const cardTipsSortedAndFiltered = getSortedAndFilteredBySortPosition(
        formattedCard,
      ) as CompareCredit.FormattedCard[]

      const withBofaExcludedOnBrave = userIsOnBrave
        ? cardTipsSortedAndFiltered.flatMap((card) =>
            card.issuer.slug.current === 'bank-of-america' ? [] : [card],
          )
        : cardTipsSortedAndFiltered

      const cardTipsWithPinsAppliedIfActive = isPinningActive
        ? pinEntities(
            withBofaExcludedOnBrave,
            formatPinnedProducts(
              pinnedCardTips?.map((pinnedCard) => {
                return {
                  card: {
                    _id: pinnedCard.card._id,
                    slug: pinnedCard.card.slug,
                  },
                  frequencyPercent: pinnedCard.frequencyPercent,
                }
              }) ?? [],
            ),
            mapEntityPositions(cards.map(({ slug }) => slug)),
          )
        : withBofaExcludedOnBrave

      return cardTipsWithPinsAppliedIfActive
    }, [
      sort,
      cardSlugs,
      referencedCards,
      userIsOnBrave,
      isPinningActive,
      pinnedCardTips,
    ])

    return (
      <>
        <section className="c-compare-v / mt-4 mb-8 leading-snug">
          <div className="c-card-table-hori__container / mb-6 / py-4 / leading-normal">
            {title && (
              <p className="c-card-table-hori__title / text-xl sm:text-2xl xl:text-3xl font-bold text-primary / mb-2 xl:mb-3">
                {title}
              </p>
            )}
            {isEmpty(cardsData) && (
              <CardComparisonTableHorizontalSkeleton
                n={
                  cardsData.length === 1
                    ? 1
                    : cardsData.length === 2
                    ? 2
                    : cardsData.length > 2
                    ? 3
                    : 4
                }
              />
            )}

            {cardsData && (
              <div
                id="table-hori-inner"
                ref={tableHoriInnerRef}
                className="c-card-table-hori__inner / relative z-10 / w-full / overflow-x-auto"
              >
                <div
                  id="table-hori-header"
                  ref={tableStickyHeaderRef}
                  className={cn(
                    `c-card-table-hori__header / lg:w-full / lg:flex lg:justify-center lg:top-0 lg:left-0 / bg-white z-40`,
                    {
                      'w-[90vw] fixed top-0 overflow-x-hidden':
                        tableHeaderIsSticky,
                    },
                  )}
                >
                  <div
                    className={cn(`lg:max-w-[1152px]`, {
                      ' lg:px-8 xl:px-4': tableHeaderIsSticky,
                    })}
                  >
                    <table className="c-card-table-hori / w-full / table table-fixed overflow-x-hidden / align-top border-collapse">
                      <thead className="c-card-table-hori__thead / text-primary text-fs12 xl:text-fs13 text-left leading-[1] align-bottom / bg-white border-b-2 border-primary">
                        <tr className="font-normal">
                          <th className="w-[10rem] lg:w-[7rem] xl:w-[9rem] / py-3 pr-3 / font-semibold">
                            Credit Card
                          </th>
                          <th className="w-[10rem] xl:w-[12.5rem] / py-3 pr-3 / font-semibold">
                            Intro APR
                          </th>
                          <th className="w-[10rem] xl:w-[12.5rem] / py-3 pr-3 / font-semibold">
                            Regular APR
                          </th>
                          <th className="w-[7rem] xl:w-[8rem] / py-3 pr-3 / font-semibold">
                            Welcome Offer
                          </th>
                          <th className="w-[5rem] xl:w-[5.5rem] / py-3 pr-3 / font-semibold">
                            Annual Fee
                          </th>
                          <th className="w-[10rem] / py-3 pr-3 / font-semibold">
                            CompareCredit Rating
                          </th>
                          <th className="w-[10rem] xl:w-[11rem] / py-3 / font-semibold / hidden lg:table-cell / align-middle">
                            Learn More
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                </div>

                <table className="c-card-table-hori / w-full / mx-auto / table table-fixed align-top border-collapse">
                  <tbody className="c-card-table-hori__tbody / align-top text-fs13 xl:text-sm leading-[1.5]">
                    {cardsData.map((card, i) => (
                      <HorizontelTable
                        key={i}
                        card={card as CompareCredit.FormattedCard}
                        cardOrderIds={cardOrderIds}
                        categoryId={categoryId}
                        externalId={externalId}
                        referencedCards={referencedCards}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </>
    )
  }
  return CardComparisonTableHorizontalRender
}
